/**
 * Facilities, Entrega 3b (ADR 0036): planos de manutenção preventiva por bem ou ambiente,
 * periodicidade configurável (D4), execuções previstas, geração manual das OS e rotina desligada
 * por padrão (D3). O cálculo das datas fica aqui para a tela, o servidor e a rotina usarem o mesmo.
 */
import { z } from 'zod';
import { FACILITIES_IMPACTS, FACILITIES_PRIORITIES } from './facilities.ts';
import { WORK_ORDER_EXECUTORS } from './facilities-maintenance.ts';

export const PLAN_FREQUENCY_UNITS = ['day', 'week', 'month', 'year'] as const;
export type PlanFrequencyUnit = (typeof PLAN_FREQUENCY_UNITS)[number];

/**
 * Base do calendário:
 * - `fixed`: datas fixas a partir da primeira (todo dia 10, a cada 3 meses...), mesmo que uma
 *   execução atrase;
 * - `after_completion`: a próxima conta a partir da conclusão da anterior.
 */
export const PLAN_SCHEDULE_BASES = ['fixed', 'after_completion'] as const;
export type PlanScheduleBasis = (typeof PLAN_SCHEDULE_BASES)[number];

/** Situação do plano: ativo gera execuções; suspenso e encerrado não (encerrado é final). */
export const PLAN_STATUSES = ['active', 'paused', 'closed'] as const;
export type PlanStatus = (typeof PLAN_STATUSES)[number];

/** Situação de uma execução prevista no calendário. */
export const PLAN_EXECUTION_STATES = [
  'scheduled',
  'due',
  'overdue',
  'generated',
  'done',
  'late_done',
  'skipped',
  'cancelled',
] as const;
export type PlanExecutionState = (typeof PLAN_EXECUTION_STATES)[number];

/** Limites: no máximo 999 unidades por intervalo e um ano de antecedência ou duração. */
export const PLAN_MAX_INTERVAL = 999;
export const PLAN_MAX_LEAD_DAYS = 365;
export const PLAN_MAX_DURATION_DAYS = 365;
/** Quantas datas futuras o calendário e a ficha mostram por plano, no máximo. */
export const PLAN_MAX_PREVIEW = 400;

const ISO = /^\d{4}-\d{2}-\d{2}$/;

function parts(date: string) {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return { y, m, d };
}

function iso(y: number, m: number, d: number) {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function daysInMonth(y: number, m: number) {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** Soma dias a uma data (AAAA-MM-DD), em UTC. */
export function addDays(date: string, days: number): string {
  const t = Date.parse(`${date}T00:00:00Z`) + days * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}

/**
 * Soma `n` unidades a uma data. Meses e anos guardam o dia de origem e, quando o mês não tem esse
 * dia, usam o último (31/01 + 1 mês = 28 ou 29/02). Sempre a partir da data de origem, nunca em
 * cascata, para o dia não "escorregar" ao longo do ano.
 */
export function addInterval(date: string, unit: PlanFrequencyUnit, n: number): string {
  if (unit === 'day') return addDays(date, n);
  if (unit === 'week') return addDays(date, n * 7);
  const { y, m, d } = parts(date);
  const months = unit === 'month' ? n : n * 12;
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return iso(ny, nm, Math.min(d, daysInMonth(ny, nm)));
}

export interface PlanSchedule {
  startDate: string;
  endDate: string | null;
  frequencyUnit: PlanFrequencyUnit;
  frequencyInterval: number;
  scheduleBasis: PlanScheduleBasis;
}

/**
 * Datas previstas de execução entre `from` e `to` (inclusive), em ordem.
 * - Calendário fixo: a primeira data e as seguintes, sempre contadas da primeira.
 * - Após a conclusão: a próxima é a última conclusão + o intervalo (ou a primeira data, se ainda
 *   não houve conclusão). Só existe uma data prevista por vez: a seguinte depende da execução.
 * Nunca passa da data final do plano nem de `PLAN_MAX_PREVIEW` datas.
 */
export function planDueDates(
  s: PlanSchedule,
  from: string,
  to: string,
  lastCompletion: string | null = null,
): string[] {
  if (!ISO.test(from) || !ISO.test(to) || to < from) return [];
  const last = s.endDate && s.endDate < to ? s.endDate : to;
  if (s.scheduleBasis === 'after_completion') {
    const next = lastCompletion
      ? addInterval(lastCompletion, s.frequencyUnit, s.frequencyInterval)
      : s.startDate;
    // Antes da primeira data não há execução prevista.
    const due = next < s.startDate ? s.startDate : next;
    return due >= from && due <= last ? [due] : [];
  }
  const out: string[] = [];
  // Salta direto para perto de `from` (dias e semanas) para não percorrer anos de datas.
  let k = 0;
  if (s.frequencyUnit === 'day' || s.frequencyUnit === 'week') {
    const step = s.frequencyInterval * (s.frequencyUnit === 'week' ? 7 : 1);
    const gap = Math.floor(
      (Date.parse(`${from}T00:00:00Z`) - Date.parse(`${s.startDate}T00:00:00Z`)) / 86_400_000,
    );
    if (gap > 0) k = Math.floor(gap / step);
  } else if (from > s.startDate) {
    const a = parts(s.startDate);
    const b = parts(from);
    const months = (b.y - a.y) * 12 + (b.m - a.m);
    const per = s.frequencyInterval * (s.frequencyUnit === 'year' ? 12 : 1);
    k = Math.max(0, Math.floor(months / per) - 1);
  }
  for (; out.length < PLAN_MAX_PREVIEW; k++) {
    const due = addInterval(s.startDate, s.frequencyUnit, k * s.frequencyInterval);
    if (due > last) break;
    if (due >= from) out.push(due);
  }
  return out;
}

/**
 * Situação de uma execução para o calendário:
 * - sem OS: prevista, dentro da antecedência (`due`) ou atrasada (data passada);
 * - com OS: gerada, concluída (no prazo ou depois da data) ou cancelada;
 * - pulada com motivo.
 */
export function planExecutionState(e: {
  dueDate: string;
  today: string;
  leadDays: number;
  skipped: boolean;
  workOrderStatus: string | null;
  finishedOn: string | null;
}): PlanExecutionState {
  if (e.skipped) return 'skipped';
  if (e.workOrderStatus) {
    if (e.workOrderStatus === 'cancelled') return 'cancelled';
    if (e.workOrderStatus === 'completed' || e.workOrderStatus === 'accepted') {
      return e.finishedOn && e.finishedOn > e.dueDate ? 'late_done' : 'done';
    }
    return e.dueDate < e.today ? 'overdue' : 'generated';
  }
  if (e.dueDate < e.today) return 'overdue';
  return addDays(e.today, e.leadDays) >= e.dueDate ? 'due' : 'scheduled';
}

// ----- Entradas -----

const optional = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const optionalId = z
  .uuid()
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const isoDate = z.string().regex(ISO, 'Data inválida');
const optionalDate = isoDate
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const money = z
  .object({
    amount: z
      .string()
      .trim()
      .regex(/^\d{1,15}(\.\d{1,4})?$/, 'Use números, com ponto nos decimais'),
    currency: z.string().regex(/^[A-Z]{3}$/, 'Moeda inválida'),
  })
  .nullable()
  .optional();

/** Plano preventivo: o que, onde, quando, quem executa e referências de custo (só informativas). */
export const maintenancePlanInput = z
  .object({
    legalEntityId: z.uuid(),
    branchId: z.uuid(),
    locationId: optionalId,
    assetId: optionalId,
    title: z.string().trim().min(1).max(200),
    description: optional(4000),
    priority: z.enum(FACILITIES_PRIORITIES).default('normal'),
    impact: z.enum(FACILITIES_IMPACTS).default('low'),
    safetyRisk: z.boolean().default(false),
    takeOutOfService: z.boolean().default(false),
    frequencyUnit: z.enum(PLAN_FREQUENCY_UNITS),
    frequencyInterval: z.number().int().min(1).max(PLAN_MAX_INTERVAL),
    scheduleBasis: z.enum(PLAN_SCHEDULE_BASES).default('fixed'),
    startDate: isoDate,
    endDate: optionalDate,
    leadDays: z.number().int().min(0).max(PLAN_MAX_LEAD_DAYS).default(7),
    durationDays: z.number().int().min(0).max(PLAN_MAX_DURATION_DAYS).default(0),
    executorType: z.enum(WORK_ORDER_EXECUTORS),
    internalOwnerUserId: z.uuid(),
    technicianUserId: optionalId,
    providerPartnerId: optionalId,
    providerTechnicianName: optional(150),
    checklistTemplateId: optionalId,
    estimated: money,
    costCenterId: optionalId,
    departmentId: optionalId,
    purchaseOrderId: optionalId,
    contractId: optionalId,
  })
  .refine((v) => !!v.assetId || !!v.locationId, {
    path: ['assetId'],
    message: 'Escolha o bem ou o ambiente do plano',
  })
  .refine((v) => !v.takeOutOfService || !!v.assetId, {
    path: ['takeOutOfService'],
    message: 'Só um bem pode ser retirado de uso',
  })
  .refine((v) => v.executorType !== 'provider' || !!v.providerPartnerId, {
    path: ['providerPartnerId'],
    message: 'Escolha o prestador no cadastro de parceiros',
  })
  .refine((v) => v.executorType !== 'internal' || !!v.technicianUserId, {
    path: ['technicianUserId'],
    message: 'Escolha o técnico executor',
  })
  .refine((v) => !v.endDate || v.endDate >= v.startDate, {
    path: ['endDate'],
    message: 'O fim não pode ser antes da primeira data',
  });
export type MaintenancePlanInput = z.infer<typeof maintenancePlanInput>;

/** Gerar a OS de uma data prevista, ou pular a data com motivo. */
export const planGenerateInput = z.object({ dueDate: isoDate });
export const planSkipInput = z.object({
  dueDate: isoDate,
  reason: z.string().trim().min(5).max(2000),
});
export const planReasonInput = z.object({ reason: z.string().trim().min(5).max(2000) });
